import { Box, Stack, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';
import { radius } from '@karehero/llama/theme/radius';

import KeniOrb, { type KeniOrbState } from '@/components/wrapped/KeniOrb';

// The conversation surface the capture frames share. Deliberately local to the
// Tell Us Once explorations rather than reaching into north-star's ChatBubble:
// what this conversation should feel like is one of the open questions, so it
// needs to be free to diverge from the Keni one while that's open.

export type Turn = {
  from: 'us' | 'user';
  text: string;
};

const Bubble = ({ turn }: { turn: Turn }) => {
  const isUs = turn.from === 'us';
  return (
    <Box
      sx={{
        alignSelf: isUs ? 'flex-start' : 'flex-end',
        maxWidth: '85%',
        px: 2,
        py: 1.25,
        bgcolor: isUs ? 'background.paper' : 'custom.coral.600',
        border: isUs ? 1 : 0,
        borderColor: 'custom.neutral.200',
        borderRadius: `${radius.lg}px`,
        // The tail corner: squared off on the side the speaker sits.
        borderBottomLeftRadius: isUs ? '4px' : '16px',
        borderBottomRightRadius: isUs ? '16px' : '4px',
      }}
    >
      <Typography
        sx={{ color: isUs ? 'text.primary' : 'custom.neutral.50' }}
        variant='body2'
      >
        {turn.text}
      </Typography>
    </Box>
  );
};

export const ChatInput = ({ placeholder }: { placeholder: string }) => (
  <Stack
    sx={{
      alignItems: 'center',
      px: 2,
      py: 1.5,
      bgcolor: 'background.paper',
      border: 1,
      borderColor: 'custom.neutral.200',
      borderRadius: '24px',
    }}
    direction='row'
    spacing={1.5}
  >
    <Typography
      sx={{ flex: 1, minWidth: 0 }}
      variant='body2'
      color='text.secondary'
    >
      {placeholder}
    </Typography>
    <Box sx={{ color: 'custom.coral.600', display: 'flex' }}>
      <Icon icon={'paper-plane-top' as never} fontSize={16} />
    </Box>
  </Stack>
);

// The voice way in: the orb is the affordance, with typing kept available
// underneath rather than replaced. A2's whole argument is that you can just
// talk, so the talk control is the primary one and the keyboard is the escape
// hatch — the reverse of every other capture screen in the prototype.
export const TalkInput = ({
  typePrompt,
  state = 'idle',
}: {
  typePrompt: string;
  state?: KeniOrbState;
}) => (
  <Stack sx={{ alignItems: 'center' }} spacing={2}>
    <KeniOrb size={64} state={state} />

    {/* The same input the other capture screens use, so the only thing that
        differs on A2 is the orb above it. */}
    <Box sx={{ alignSelf: 'stretch' }}>
      <ChatInput placeholder={typePrompt} />
    </Box>
  </Stack>
);

const Chat = ({ turns }: { turns: Turn[] }) => (
  <Stack sx={{ py: 1 }} spacing={1.5}>
    {turns.map((turn, i) => (
      <Bubble key={i} turn={turn} />
    ))}
  </Stack>
);

Chat.displayName = 'Chat';
export default Chat;
